import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaBars, FaSignOutAlt, FaUserCircle } from "react-icons/fa";
import logo from "../assets/logo.jpeg";
import { fetchCurrentUser } from "../api/user-api";

const Sidebar = ({
  role = "field_staff",
  menuItems = [],
  onLogout = () => {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return setUser(null);

      try {
        const userInfo = await fetchCurrentUser(token);
        if (userInfo) {
          setUser({
            firstName: userInfo.firstName,
            lastName: userInfo.lastName,
            email: userInfo.email,
            profilePic: userInfo.profilePic,
          });
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  const toggleMenu = (label) => setOpenMenus((prev) => ({ ...prev, [label]: !prev[label] }));
  const toggleSidebar = () => setCollapsed(!collapsed);
  const toggleMobileSidebar = () => setMobileOpen(!mobileOpen);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
    onLogout();
  };

  const filteredItems = menuItems.filter(
    (item) => !item.roles || item.roles.includes(role)
  );

  return (
    <>
      {/* Mobile Hamburger */}
      <button
        onClick={toggleMobileSidebar}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded shadow"
      >
        <FaBars />
      </button>

      {/* Overlay for mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={toggleMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 left-0 z-50 h-screen bg-white shadow-md flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${collapsed ? "w-20" : "w-64"}
          md:translate-x-0
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:relative
        `}
      >
        {/* Top Section */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-6 border-b">
          {!collapsed && <img src={logo} alt="Logo" className="w-28" />}
          <div className="flex gap-2">
            <button
              onClick={toggleSidebar}
              className="text-gray-600 hover:text-orange-500 hidden md:block"
              title={collapsed ? "Expand" : "Collapse"}
            >
              <FaBars />
            </button>
            <button
              onClick={toggleMobileSidebar}
              className="text-gray-600 hover:text-orange-500 md:hidden"
            >
              <FaChevronDown />
            </button>
          </div>
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto px-2">
          <ul className="space-y-1">
            {filteredItems.map((item, index) => {
              const isActive = item.path && location.pathname === item.path;
              const hasChildren = Array.isArray(item.children);
              const isOpen = openMenus[item.label];

              return (
                <div key={index}>
                  <Link to={item.path} onClick={() => setMobileOpen(false)}>
                    <div
                      className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-all duration-200
                        ${isActive
                          ? "bg-orange-600 text-white font-semibold"
                          : "text-black hover:bg-orange-600 hover:text-white"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon}
                        {!collapsed && <span>{item.label}</span>}
                      </div>
                      {hasChildren && !collapsed && (
                        <button onClick={() => toggleMenu(item.label)}>
                          <FaChevronDown className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                      )}
                    </div>
                  </Link>

                  {/* Children */}
                  {isOpen && !collapsed && (
                    <div className="pl-6 mt-1 space-y-1">
                      {item.children.map((child, idx) => (
                        <Link key={idx} to={child.path} onClick={() => setMobileOpen(false)}>
                          <div
                            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all duration-200
                              ${location.pathname === child.path
                                ? "bg-orange-600 text-white font-semibold"
                                : "text-black hover:bg-orange-600 hover:text-white"
                              }`}
                          >
                            {child.icon}
                            <span>{child.label}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </ul>
        </div>

        {/* Bottom User & Logout */}
        <div className="flex-shrink-0 px-4 py-4 border-t">
          {user ? (
            <div className="flex items-center gap-3 mb-4">
              {!collapsed && (
                <div>
                  <p className="text-sm font-semibold">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="animate-pulse flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              {!collapsed && (
                <div>
                  <div className="w-24 h-4 bg-gray-200 rounded mb-1"></div>
                  <div className="w-32 h-4 bg-gray-200 rounded"></div>
                </div>
              )}
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center w-full gap-3 px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-all duration-200"
          >
            <FaSignOutAlt />
            {!collapsed && "Logout"}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
